import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Share2, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Tests the configured MAKE_WEBHOOK_URL (or an ad-hoc URL the admin pastes here).
 * Useful while setting up the Make.com scenario — Marta can fire a test payload
 * and confirm her scenario receives it.
 */
const SocialWebhookTester = ({ password, loggedIn }) => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  if (!loggedIn) return null;

  const fire = async () => {
    setLoading(true);
    setLastResult(null);
    try {
      const { data } = await axios.post(`${API}/newsletter/admin/social-webhook/test`, {
        password,
        webhook_url: url || undefined,
      });
      setLastResult(data);
      if (data.ok) {
        toast.success(`Webhook OK · status ${data.status}`);
      } else if (data.status === "not-configured") {
        toast.error("No has configurado MAKE_WEBHOOK_URL aún. Pega una URL arriba o añade la variable en .env.");
      } else {
        toast.error(`Falló · ${data.error || data.status}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al disparar el webhook");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="bg-white rounded-3xl border border-sand/50 mt-10 overflow-hidden"
      data-testid="social-webhook-tester"
    >
      <div className="p-7 border-b border-sand/40">
        <div className="flex items-center gap-3 flex-wrap">
          <Share2 size={18} className="text-terracotta" />
          <h2 className="font-serif text-2xl text-charcoal">Cross-post automático en redes</h2>
          <span className="text-[11px] text-charcoal/55 italic">(Make.com / Zapier)</span>
        </div>
        <p className="text-sm text-charcoal/60 mt-3 leading-relaxed">
          Cuando la newsletter mensual se envía sola, el backend dispara un webhook con el contenido
          ({"{subject, month_label, subscribers_count, ...}"}) que tu escenario de Make.com puede usar para
          publicar una story / post en Instagram + LinkedIn. Lee la guía completa en{" "}
          <code className="text-terracotta">/app/memory/MAKE_WEBHOOK_SETUP.md</code>.
        </p>
      </div>

      <div className="p-7 space-y-4">
        <div>
          <label className="text-[11px] tracking-[0.28em] uppercase text-charcoal/60 mb-2 block">
            URL de prueba (opcional — sólo para validar tu escenario antes de añadirla al .env)
          </label>
          <input
            type="url"
            placeholder="https://hook.eu1.make.com/xxxxxxxxxxx"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            data-testid="webhook-url-input"
            className="w-full px-4 py-3 rounded-xl border border-sand/80 focus:border-terracotta focus:ring-1 focus:ring-terracotta outline-none text-charcoal text-sm"
          />
          <p className="text-[12px] text-charcoal/50 mt-2">
            Vacío = usa la variable <code className="text-terracotta">MAKE_WEBHOOK_URL</code> del{" "}
            <code className="text-terracotta">.env</code>.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={fire}
            disabled={loading}
            data-testid="fire-webhook-btn"
            className="inline-flex items-center gap-2 bg-charcoal text-ivory rounded-full px-6 py-2.5 text-sm font-medium hover:bg-terracotta transition-colors disabled:opacity-50"
          >
            <Share2 size={14} /> {loading ? "Disparando…" : "Disparar payload de prueba"}
          </button>
          <a
            href="https://www.make.com/en/help/tools/webhooks"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-charcoal/20 text-charcoal rounded-full px-5 py-2.5 text-sm hover:border-charcoal/50 transition-colors"
          >
            Make.com webhooks <ExternalLink size={13} />
          </a>
        </div>

        {lastResult && (
          <div
            className="bg-cream/40 border border-sand/60 rounded-xl p-4 text-[12.5px] font-mono text-charcoal/85 overflow-auto"
            data-testid="webhook-result"
          >
            <pre className="whitespace-pre-wrap">{JSON.stringify(lastResult, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialWebhookTester;
